import * as React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputBase,
  Link,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment";
import { useLocation, Link as RouterLink } from "react-router-dom";
import { AuthenticationContext, ImagesContext } from "../Context";
import { getHeaders } from "../utils/HeaderHelper";
import { snakeCaseToTitleCase } from "../utils/CaseFormatHelper";
import CustomChip from "../shared/CustomChip";
import DatePickerWithLabel from "../common/DatePickerWithLabel";
import BadgeList from "../common/BadgeList";
import LoadingReportsTable from "../shared/LoadingReportsTable";
import EmptyReportsTable from "../shared/EmptyReportsTable";
import SupplyRequestsBillingAdvancedFilters from "./SupplyRequestsBillingAdvancedFilters";

const DESCRIPTION_CHAR_LIMIT = 150;

interface FilterOption {
  id: string;
  name: string;
}

interface FilterOptions {
  payer_programs: FilterOption[];
  statuses: FilterOption[];
  request_authors: FilterOption[];
}

interface SupplyRequestRow {
  patient_name: string | null;
  patient_id: number | null;
  date_of_birth: string | null;
  member_unique_id: string | null;
  enrolled_payor_programs: string | null;
  status: string | null;
  request_date: string | null;
  item: string | null;
  item_quantity: number | null;
  tracking_number: string | null;
  description: string | null;
  request_author: string | null;
  encounter_billing_uuid: string | null;
  supply_request_id: number | null;
}

interface ChipData {
  key: string;
  label: string;
  type: string;
}

const getStatusChipType = (status: string | null) => {
  switch (status) {
    case "completed": return "success";
    case "canceled":  return "error";
    case "new":       return "warning";
    default:          return "info";
  }
};

const DescriptionCell: React.FC<{ text: string | null }> = ({ text }) => {
  const [open, setOpen] = React.useState(false);
  if (!text) return <TableCell align="left"></TableCell>;
  const isLong = text.length > DESCRIPTION_CHAR_LIMIT;
  return (
    <TableCell align="left">
      {isLong ? (
        <>
          {text.slice(0, DESCRIPTION_CHAR_LIMIT)}...{" "}
          <Button
            size="small"
            variant="text"
            onClick={() => setOpen(true)}
            sx={{ minWidth: "auto", padding: "0 4px", fontSize: "12px" }}
          >
            Read More
          </Button>
          <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              Description
              <IconButton
                aria-label="close"
                onClick={() => setOpen(false)}
                sx={{ position: "absolute", right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Typography sx={{ whiteSpace: "pre-wrap" }}>{text}</Typography>
            </DialogContent>
          </Dialog>
        </>
      ) : (
        text
      )}
    </TableCell>
  );
};

const lastMonthStart = () => moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD");
const lastMonthEnd = () => moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD");

const SupplyRequestsBillingReport: React.FC = () => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const images = React.useContext(ImagesContext);
  const location = useLocation();

  const debounceTimeout = React.useRef<NodeJS.Timeout | null>(null);
  const fetchSeqRef = React.useRef(0);


  const { urlPayerId, urlPayerName, urlStartDate, urlEndDate, urlStatuses } = React.useMemo(() => {
    const p = new URLSearchParams(location.search);
    return {
      urlPayerId: p.get("payer_id") || "",
      urlPayerName: p.get("payer_name") || "",
      urlStartDate: p.get("start_date") || "",
      urlEndDate: p.get("end_date") || "",
      urlStatuses: p.getAll("statuses[]"),
    };
  }, [location.search]);

  const [filterOptions, setFilterOptions] = React.useState<FilterOptions>({
    payer_programs: [],
    statuses: [],
    request_authors: [],
  });
  const [payerName, setPayerName] = React.useState<string>(urlPayerName);
  const [tableData, setTableData] = React.useState<SupplyRequestRow[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [pageCount, setPageCount] = React.useState<number>(0);
  const [totalCount, setTotalCount] = React.useState<number>(0);
  const [perPage, setPerPage] = React.useState<number>(100);

  // Filters — default to last full calendar month; URL params override on direct navigation
  const [startDate, setStartDate] = React.useState<string>(urlStartDate || lastMonthStart());
  const [endDate, setEndDate] = React.useState<string>(urlEndDate || lastMonthEnd());
  const payerIdFilter = urlPayerId;
  const backHref = urlPayerId ? "/health-navigator-summary-report#billing-summary" : "/reports";
  const [lastName, setLastName] = React.useState<string>("");
  const [debouncedLastName, setDebouncedLastName] = React.useState<string>("");

  // Advanced search state
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedPayerPrograms, setSelectedPayerPrograms] = React.useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = React.useState<string[]>(urlStatuses);
  const [selectedRequestAuthors, setSelectedRequestAuthors] = React.useState<string[]>([]);
  const [badgeData, setBadgeData] = React.useState<ChipData[]>([]);

  // Sort state
  const [sortObject, setSortObject] = React.useState<{ field: string; direction: string }>({
    field: "",
    direction: "ascending",
  });

  // Debounce last name
  React.useEffect(() => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      if (lastName.length === 0 || lastName.length >= 2) {
        setDebouncedLastName(lastName);
      }
    }, 500);
    return () => { if (debounceTimeout.current) clearTimeout(debounceTimeout.current); };
  }, [lastName]);

  // Reset page on filter change
  React.useLayoutEffect(() => {
    setCurrentPage(1);
  }, [startDate, endDate, debouncedLastName, selectedPayerPrograms, selectedStatuses, selectedRequestAuthors, perPage, sortObject]);

  // Build badge chips
  React.useEffect(() => {
    const ppMap = filterOptions.payer_programs.reduce<Record<string, string>>((acc, o) => {
      acc[o.id] = o.name; return acc;
    }, {});
    const stMap = filterOptions.statuses.reduce<Record<string, string>>((acc, o) => {
      acc[o.id] = o.name; return acc;
    }, {});
    const raMap = filterOptions.request_authors.reduce<Record<string, string>>((acc, o) => {
      acc[o.id] = o.name; return acc;
    }, {});

    const badges: ChipData[] = [
      ...selectedPayerPrograms.map((id) => ({ key: `pp_${id}`, label: ppMap[id] || id, type: "payer_program" })),
      ...selectedStatuses.map((id) => ({ key: `st_${id}`, label: stMap[id] || id, type: "status" })),
      ...selectedRequestAuthors.map((id) => ({ key: `ra_${id}`, label: raMap[id] || id, type: "request_author" })),
    ];
    setBadgeData(badges);
  }, [selectedPayerPrograms, selectedStatuses, selectedRequestAuthors, filterOptions]);

  const handleDeleteBadge = (badge: ChipData) => {
    const rawId = badge.key.replace(/^(pp_|st_|ra_)/, "");
    if (badge.type === "payer_program") setSelectedPayerPrograms((prev) => prev.filter((id) => id !== rawId));
    if (badge.type === "status") setSelectedStatuses((prev) => prev.filter((id) => id !== rawId));
    if (badge.type === "request_author") setSelectedRequestAuthors((prev) => prev.filter((id) => id !== rawId));
  };

  const setSortOrder = (sortBy: string, direction: string) => {
    setSortObject({ field: sortBy, direction });
  };

  const sortIcon = (field: string) => {
    if (sortObject.field !== field) {
      return <img src={images.sort_plain_src} width="10" className="sort-icon" alt="Sort" />;
    }
    return sortObject.direction === "ascending"
      ? <img src={images.sort_ascending_src} width="10" className="sort-icon" alt="Asc" />
      : <img src={images.sort_descending_src} width="10" className="sort-icon" alt="Desc" />;
  };


  const prevPayerIdRef = React.useRef(payerIdFilter);
  const isInitialMount = React.useRef(true);
  React.useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else if (prevPayerIdRef.current !== payerIdFilter) {

      setPayerName(urlPayerName);
      setTableData([]);
      setTotalCount(0);
      setPageCount(0);
      setFilterOptions({ payer_programs: [], statuses: [], request_authors: [] });

      if (selectedPayerPrograms.length > 0) setSelectedPayerPrograms([]);
      if (selectedRequestAuthors.length > 0) setSelectedRequestAuthors([]);

      setSelectedStatuses(urlStatuses);
    }
    prevPayerIdRef.current = payerIdFilter;

    const seq = ++fetchSeqRef.current;
    const controller = new AbortController();
    setLoading(true);

    const params = new URLSearchParams({
      start_date:     startDate || "",
      end_date:       endDate || "",
      last_name:      debouncedLastName.length >= 2 ? debouncedLastName : "",
      page:           currentPage.toString(),
      per_page:       perPage.toString(),
      sort_by:        sortObject.field,
      sort_direction: sortObject.direction,
    });
    if (payerIdFilter) params.set("payer_id", payerIdFilter);
    selectedPayerPrograms.forEach((id) => params.append("payer_program_ids[]", id));
    selectedStatuses.forEach((id) => params.append("statuses[]", id));
    selectedRequestAuthors.forEach((id) => params.append("request_author_ids[]", id));

    fetch(`/admin/reports/supply_requests_billing/report_data?${params}`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
      signal: controller.signal,
    })
      .then((res) => res.json())
      .then((res) => {
        if (seq !== fetchSeqRef.current) return;
        if (!res.success) {
          console.log(res.error);
          setTableData([]);
          setPageCount(0);
          setTotalCount(0);
          return;
        }
        setTableData(res.resource?.results || []);
        setPageCount(res.resource?.total_pages || 0);
        setTotalCount(res.resource?.total_count || 0);
        if (res.resource?.filter_options) setFilterOptions(res.resource.filter_options);
        if (res.resource?.payer_name) setPayerName(res.resource.payer_name);
      })
      .catch((error) => {
        if ((error as Error).name === "AbortError") return;
        console.error("Failed to fetch report data:", error);
        if (seq === fetchSeqRef.current) {
          setTableData([]);
          setPageCount(0);
          setTotalCount(0);
        }
      })
      .finally(() => {
        if (seq === fetchSeqRef.current) setLoading(false);
      });

    return () => controller.abort();
  }, [
    startDate, endDate, debouncedLastName,
    selectedPayerPrograms, selectedStatuses, selectedRequestAuthors,
    currentPage, perPage, sortObject,
    authenticationSetting.csrfToken, payerIdFilter,
  ]);

  const handleExportCsv = () => {
    const params = new URLSearchParams({
      start_date:     startDate || "",
      end_date:       endDate || "",
      last_name:      debouncedLastName.length >= 2 ? debouncedLastName : "",
      sort_by:        sortObject.field,
      sort_direction: sortObject.direction,
    });
    if (payerIdFilter) params.set("payer_id", payerIdFilter);
    selectedPayerPrograms.forEach((id) => params.append("payer_program_ids[]", id));
    selectedStatuses.forEach((id) => params.append("statuses[]", id));
    selectedRequestAuthors.forEach((id) => params.append("request_author_ids[]", id));
    window.location.href = `/admin/reports/supply_requests_billing/generate_csv.csv?${params}`;
  };

  const handleOpenFilter = (event: React.MouseEvent<HTMLElement>) => {
    if (loading) return;
    setAnchorEl(event.currentTarget);
  };

  const reportTitle = payerName
    ? `${payerName} Supply Requests Billing Report`
    : payerIdFilter
      ? "Loading Report…"
      : "Supply Requests Billing Report";

  return (
    <Box className="report-main-content" sx={{ paddingLeft: "120px !important" }}>
      <Box className="main-header">
        <Box className="heading-with-back-button-section">
          <Stack direction="row" columnGap="12px">
            <Link
              component={RouterLink}
              to={backHref}
              style={{ textDecoration: "none", display: "flex", alignItems: "center" }}
            >
              <ArrowBackIosNewIcon
                style={{
                  borderRadius: "3px",
                  padding: "8px",
                  color: "black",
                  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  fontSize: "14px",
                }}
              />
            </Link>
            <Typography
              sx={{ fontSize: "24px", color: "black", fontWeight: 700, lineHeight: "normal" }}
            >
              {reportTitle}
            </Typography>
          </Stack>
        </Box>
      </Box>

      <Box className="report-details">
        <Box className="sub-header">
          <Box className="subheading-with-pill-section">
            <Typography className="sub-heading">Report Details</Typography>
            <Box className="count-pill">{totalCount}</Box>
          </Box>

          <Box className="filters-section">
            <Paper className="input-paper">
              <InputBase
                placeholder="Search Last Name"
                inputProps={{ "aria-label": "search last name" }}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
              <SearchIcon sx={{ color: "#707070" }} />
            </Paper>
            <DatePickerWithLabel
              date={startDate}
              setDate={setStartDate}
              initialDate={lastMonthStart()}
              label="Start Date"
              disabled={loading}
            />
            <DatePickerWithLabel
              date={endDate}
              setDate={setEndDate}
              initialDate={lastMonthEnd()}
              label="End Date"
              disabled={loading}
            />
            <div
              className="advanced-search-container"
              onClick={loading ? undefined : handleOpenFilter}
              style={{ cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.8 : 1 }}
            >
              <FilterListIcon sx={{ width: 20, height: 20 }} />
              <p className="advanced-search-text">Advanced Search</p>
              {anchorEl ? (
                <ArrowDropUp sx={{ width: 24, height: 24 }} />
              ) : (
                <ArrowDropDownIcon sx={{ width: 24, height: 24 }} />
              )}
              <SupplyRequestsBillingAdvancedFilters
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
                payerProgramOptions={filterOptions.payer_programs}
                statusOptions={filterOptions.statuses}
                requestAuthorOptions={filterOptions.request_authors}
                selectedPayerPrograms={selectedPayerPrograms}
                setSelectedPayerPrograms={setSelectedPayerPrograms}
                selectedStatuses={selectedStatuses}
                setSelectedStatuses={setSelectedStatuses}
                selectedRequestAuthors={selectedRequestAuthors}
                setSelectedRequestAuthors={setSelectedRequestAuthors}
                disabled={loading}
              />
            </div>
            <Button
              className="export-csv-button"
              onClick={handleExportCsv}
              disabled={loading}
            >
              Export as CSV
            </Button>
          </Box>
        </Box>

        <Box className="table-section">
          {badgeData.length > 0 && (
            <div className="badge-container" style={{ width: "auto", marginBottom: "20px" }}>
              <BadgeList
                badgeData={badgeData}
                setBadgeData={setBadgeData}
                handleDeleteBadge={handleDeleteBadge}
                background="none"
              />
            </div>
          )}

          {loading ? (
            <LoadingReportsTable isBadgeShowing={badgeData.length > 0} />
          ) : (
            <>
              {tableData.length > 0 ? (
                <TableContainer
                  className="report-table-container"
                  style={{ height: badgeData.length > 0 ? "45vh" : "53.8vh" }}
                >
                  <Table stickyHeader>
                    <TableHead className="table-header">
                      <TableRow sx={{ "& td, & th": { borderBottom: "none" } }}>
                        <TableCell
                          align="left"
                          sx={{ minWidth: "160px", cursor: "pointer" }}
                          onClick={() =>
                            setSortOrder(
                              "patient_name",
                              sortObject.field === "patient_name" && sortObject.direction === "ascending"
                                ? "descending"
                                : "ascending"
                            )
                          }
                        >
                          <div className="row" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <span>Patient Name</span>
                            <span className="sortIndicator">{sortIcon("patient_name")}</span>
                          </div>
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "120px" }}>Date of Birth</TableCell>
                        <TableCell align="left" sx={{ minWidth: "140px" }}>Member Unique ID</TableCell>
                        <TableCell align="left" sx={{ minWidth: "200px" }}>Enrolled Payor Program(s)</TableCell>
                        <TableCell align="left" sx={{ minWidth: "110px" }}>Status</TableCell>
                        <TableCell align="left" sx={{ minWidth: "120px" }}>Request Date</TableCell>
                        <TableCell align="left" sx={{ minWidth: "150px" }}>Item</TableCell>
                        <TableCell align="left" sx={{ minWidth: "110px" }}>Item Quantity</TableCell>
                        <TableCell align="left" sx={{ minWidth: "140px" }}>Tracking Number</TableCell>
                        <TableCell align="left" sx={{ minWidth: "520px" }}>Description</TableCell>
                        <TableCell align="left" sx={{ minWidth: "150px" }}>Request Author</TableCell>
                        <TableCell align="left" sx={{ minWidth: "170px" }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableData.map((row, idx) => (
                        <TableRow key={idx} sx={{ "& td, & th": { borderBottom: "none" } }}>
                          <TableCell align="left">
                            {row.encounter_billing_uuid && row.patient_id ? (
                              <Link
                                href={`/patient_reports/${row.patient_id}/patient_encounters/new?encounter_id=${row.encounter_billing_uuid}&supply_request_id=${row.supply_request_id ?? ""}&view_only=1`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  color: "black",
                                  textDecorationColor: "black",
                                  textDecorationLine: "underline",
                                  cursor: "pointer",
                                }}
                              >
                                {row.patient_name}
                              </Link>
                            ) : (
                              row.patient_name
                            )}
                          </TableCell>
                          <TableCell align="left">{row.date_of_birth}</TableCell>
                          <TableCell align="left">{row.member_unique_id}</TableCell>
                          <TableCell align="left">{row.enrolled_payor_programs}</TableCell>
                          {/* Thin vertical separator starts here (body only) */}
                          <TableCell align="left" sx={{ borderLeft: "1px solid #e0e0e0", minWidth: "110px" }}>
                            <CustomChip
                              label={snakeCaseToTitleCase(row.status || "")}
                              type={getStatusChipType(row.status)}
                            />
                          </TableCell>
                          <TableCell align="left">{row.request_date}</TableCell>
                          <TableCell align="left">{row.item}</TableCell>
                          <TableCell align="left">{row.item_quantity}</TableCell>
                          <TableCell align="left">{row.tracking_number}</TableCell>
                          <DescriptionCell text={row.description} />
                          <TableCell align="left">{row.request_author}</TableCell>
                          <TableCell align="left">
                            {row.encounter_billing_uuid && row.patient_id && (
                              <Link
                                href={`/patient_reports/${row.patient_id}/patient_encounters/new?encounter_id=${row.encounter_billing_uuid}&supply_request_id=${row.supply_request_id ?? ""}&view_only=1`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  color: "#0B34D8",
                                  fontSize: "13px",
                                  fontWeight: 500,
                                  textDecorationLine: "underline",
                                  textDecorationColor: "#0B34D8",
                                  cursor: "pointer",
                                }}
                              >
                                View Supply Request
                                <OpenInNewIcon sx={{ fontSize: 14 }} />
                              </Link>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <EmptyReportsTable isBadgeShowing={badgeData.length > 0} />
              )}
            </>
          )}

          {tableData.length > 0 && !loading && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                marginTop: 2,
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>Rows per page</Typography>
              <Select
                value={perPage}
                onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}
                disabled={loading}
                size="small"
                sx={{
                  minWidth: 70,
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
                }}
              >
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={150}>150</MenuItem>
                <MenuItem value={200}>200</MenuItem>
              </Select>
              {pageCount > 0 && (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Pagination
                    count={pageCount}
                    page={currentPage}
                    onChange={(_event: React.ChangeEvent<unknown>, page: number) => {
                      if (page >= 1 && page <= pageCount) setCurrentPage(page);
                    }}
                    boundaryCount={1}
                    siblingCount={1}
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SupplyRequestsBillingReport;
